// frontend/src/components/ReviewModal/ReviewModal.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Rating,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Star } from '@mui/icons-material';
import { reviewsAPI } from '../../services/api';

const ReviewModal = ({ open, onClose, seller, property, onReviewCreated }) => {
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!comment.trim()) {
      setError('Por favor, escreva um comentário');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Garantir que estamos enviando apenas o ID numérico
      const sellerId = typeof seller === 'object' ? (seller.id || seller) : seller;
      const propertyId = typeof property === 'object' ? property.id : property;

      const response = await reviewsAPI.create({
        seller_id: sellerId,
        property_id: propertyId,
        rating: rating,
        title: title,
        comment: comment,
      });

      // Resetar form
      setRating(5);
      setTitle('');
      setComment('');

      // Notificar sucesso
      if (onReviewCreated) {
        onReviewCreated(response.data);
      }

      onClose();
    } catch (err) {
      console.error('Erro ao criar avaliação:', err);

      // Tratamento de erro
      if (err.response?.status === 401) {
        setError('Sessão expirada. Faça login novamente.');
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data) {
        // Mostrar todos os erros de validação
        const errors = Object.values(err.response.data).flat().join(', ');
        setError(errors);
      } else {
        setError('Erro ao enviar avaliação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Avaliar {seller?.username}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {property && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Avaliação sobre: <strong>{property.title}</strong>
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography component="legend" gutterBottom>
            Sua avaliação *
          </Typography>
          <Rating
            value={rating}
            onChange={(event, newValue) => {
              setRating(newValue || 1);
            }}
            size="large"
            icon={<Star fontSize="inherit" />}
          />
          <Typography variant="caption" color="text.secondary" display="block">
            {rating === 1 && 'Muito insatisfeito'}
            {rating === 2 && 'Insatisfeito'}
            {rating === 3 && 'Regular'}
            {rating === 4 && 'Satisfeito'}
            {rating === 5 && 'Muito satisfeito'}
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Título da avaliação (opcional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex: Excelente atendimento!"
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 100 }}
        />

        <TextField
          fullWidth
          label="Seu comentário *"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          multiline
          rows={4}
          placeholder="Conte sua experiência com este vendedor..."
          required
          inputProps={{ maxLength: 1000 }}
          helperText={`${comment.length}/1000 caracteres`}
        />
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !comment.trim()}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Enviando...' : 'Enviar Avaliação'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewModal;